import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input, Label } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Upload, Sparkles, CheckCircle2, Trash2, Image as ImageIcon, FileType, Activity, AlertTriangle, CalendarRange, PencilLine, RefreshCw, ArrowRight } from "lucide-react";
import { formatNum } from "../lib/utils";
import { toast } from "sonner";

const SCOPE_FOR = { water: 3, electricity: 2, "natural gas": 1, lpg: 1, diesel: 1, petrol: 1, "district cooling": 2 };

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState(false);
  const [extraction, setExtraction] = useState(null);   // the uploaded document
  const [items, setItems] = useState([]);               // one editable form per utility
  const [confirming, setConfirming] = useState(null);   // item_id being committed
  const [replaceMode, setReplaceMode] = useState(false); // slice 4 — supersede the committed record
  const [replaceTargets, setReplaceTargets] = useState({}); // idx -> committed record snapshot (Bill Diff)
  const [factors, setFactors] = useState([]);
  const [groups, setGroups] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get("doc");
  const highlightRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/documents");
    setDocs(data.documents);
    api.get("/documents/health").then((r) => setHealth(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/emissions/factors").then((r) => setFactors(r.data.factors));
    api.get("/facility-groups").then((r) => setGroups(r.data.flat || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!highlightId || !docs.length) return;
    const t = setTimeout(() => highlightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
    const clear = setTimeout(() => setSearchParams({}, { replace: true }), 4000);
    return () => { clearTimeout(t); clearTimeout(clear); };
  }, [highlightId, docs]);

  const itemToForm = (ex) => ({
    item_id: ex.item_id || null,
    item_status: ex.item_status || "pending",
    document_type: ex.document_type || "",
    vendor: ex.vendor || "",
    activity_type: ex.activity_type || "",
    category: ex.category || "",
    scope: ex.scope || SCOPE_FOR[(ex.activity_type || "").toLowerCase()] || 2,
    value: ex.value ?? "",
    unit: ex.unit || "",
    factor_code: ex.suggested_factor_code || "",
    suggested_factor_code: ex.suggested_factor_code || "",
    factor_resolution: ex.factor_resolution || "",
    facility: ex.facility || ex.facility_raw || "",
    facility_options: ex.facility_options || [],
    facility_raw: ex.facility_raw || "",
    facility_resolved: ex.facility_resolved,
    facility_match_source: ex.facility_match_source || "",
    facility_is_new_site: ex.facility_is_new_site || false,
    facility_ambiguous: ex.facility_ambiguous || false,
    reconcile: ex.reconcile || null,
    date_flag: ex.date_flag || null,
    account_number: ex.account_number || "",
    service_address: ex.service_address || "",
    period: ex.period || "",
    period_start: ex.period_start || "",
    period_end: ex.period_end || "",
    currency_amount: ex.currency_amount ?? "",
    currency: ex.currency || "SGD",
    read_type: ex.read_type || "actual",
    confidence: ex.confidence || "medium",
    extraction_notes: ex.extraction_notes || "",
  });

  const onPick = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/documents/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setExtraction(data);
      const list = data.extracted_items || (data.extracted ? [data.extracted] : []);
      setItems(list.map(itemToForm));
      if (data.status === "extraction_failed") toast.error("AI could not read this document — fill in the fields manually before confirming");
      else if (data.duplicate_warning) toast.warning("This bill looks like a re-upload — check the duplicate notice before committing");
      else toast.success(`Found ${list.length} utilit${list.length === 1 ? "y" : "ies"} on this bill — review and confirm each`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Extraction failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const setItem = (idx, patch) => setItems((p) => p.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  // Bill Diff — when this bill is a re-upload of a COMMITTED bill, fetch the committed
  // record(s) it would supersede so the reviewer can compare old vs new before Replacing.
  const targetSig = items.map((f) => [f.account_number, f.invoice_number, f.period, f.activity_type].join("|")).join("~");
  useEffect(() => {
    if (!extraction?.duplicate_of?.committed || !items.length) { setReplaceTargets({}); return; }
    let cancelled = false;
    (async () => {
      const out = {};
      for (let i = 0; i < items.length; i++) {
        const f = items[i];
        if (!f.account_number && !f.invoice_number) continue;
        try {
          const { data } = await api.post("/documents/replace-target", {
            account_number: f.account_number || null, invoice_number: f.invoice_number || null,
            period: f.period, activity_type: f.activity_type });
          if (data.found) out[i] = data.record;
        } catch { /* best-effort */ }
      }
      if (!cancelled) setReplaceTargets(out);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraction, targetSig]);

  const confirmItem = async (idx) => {
    const f = items[idx];
    setConfirming(f.item_id || "single");
    // RD-28 — tell the backend to create a reusable master facility when the reviewer is
    // confirming a brand-new site (the facility name isn't one of the existing options).
    const isNewSite = !!f.facility_is_new_site && !!f.facility && !(f.facility_options || []).includes(f.facility);
    try {
      const { data } = await api.post("/documents/confirm", {
        extraction_id: extraction.id,
        item_id: f.item_id,
        period: f.period || null,
        period_start: f.period_start || null,
        period_end: f.period_end || null,
        category: f.category,
        scope: parseInt(f.scope),
        activity_type: f.activity_type,
        value: parseFloat(f.value),
        unit: f.unit,
        factor_code: f.factor_code || null,
        facility: f.facility || null,
        currency_amount: f.currency_amount === "" ? null : parseFloat(f.currency_amount),
        currency: f.currency || null,
        read_type: f.read_type || "actual",
        new_site: isNewSite,
        group_id: isNewSite ? (f.group_id || null) : null,
        replace: replaceMode,
      });
      setItem(idx, { item_status: "confirmed" });
      toast.success(
        data.superseded_count
          ? `${f.activity_type || "Utility"} replaced the committed record (superseded — kept in history, excluded from totals)`
          : `${f.activity_type || "Utility"} committed as Unverified${isNewSite ? ` — new site "${f.facility}" created` : ""} — visible in Data Collection`
      );
      if (data.document_status === "confirmed") { setExtraction(null); setItems([]); setReplaceMode(false); }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setConfirming(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/documents/${id}`);
    load();
  };

  const reopen = async (id) => {
    try {
      const { data } = await api.get(`/documents/${id}/reopen`);
      setExtraction(data);
      const list = data.extracted_items || (data.extracted ? [data.extracted] : []);
      setItems(list.map(itemToForm));
      if (data.duplicate_warning) toast.warning("This bill looks like a re-upload — check the duplicate notice before committing");
      else toast.success("Reopened for review — finish confirming the remaining utilities");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reopen this upload");
    }
  };

  const dup = extraction?.duplicate_of;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div>
        <div className="label-eyebrow mb-1">Module · OCR + LLM Extraction</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Document Inbox</h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
          Drop utility bills, fuel invoices and water bills. AI vision extracts every utility on the bill — electricity, water and gas — and creates <b>Unverified</b> data points after your review.
        </p>
      </div>

      {/* Upload zone */}
      <div className="card-flat p-8 bg-surface border-dashed border-2 text-center" data-testid="upload-zone">
        <div className="flex flex-col items-center gap-3">
          <div className="h-14 w-14 bg-accent/10 border border-accent/30 rounded-sm flex items-center justify-center">
            <Upload className="h-6 w-6 text-accent" />
          </div>
          <h3 className="font-display font-semibold text-xl">Drop a bill or invoice</h3>
          <p className="text-sm text-muted-foreground max-w-md">PDF, PNG, JPG, WebP — up to 10MB. Extraction takes 10–25 seconds.</p>
          <label className="inline-block">
            <input type="file" accept="application/pdf,image/png,image/jpeg,image/webp" onChange={onPick} className="sr-only" disabled={busy} data-testid="document-file-input" />
            <span className={`inline-flex items-center gap-2 px-4 h-10 rounded-sm bg-accent text-accent-foreground font-semibold text-sm cursor-pointer ${busy ? "opacity-50" : "hover:bg-accent/90"}`}>
              <Sparkles className="h-3.5 w-3.5" /> {busy ? "Extracting…" : "Choose file"}
            </span>
          </label>
        </div>
      </div>

      {/* Ingestion health panel */}
      {health && (
        <div data-testid="ingestion-health-panel">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="h-3.5 w-3.5 text-accent" />
            <div className="label-eyebrow">Ingestion health — QA drift monitor</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <HealthTile label="Extracted documents" value={`${health.documents_extracted}/${health.documents_total}`} sub="AI-readable uploads" testid="ih-extracted" />
            <HealthTile label="Factors auto-resolved" value={health.factor_auto_resolved} sub={health.factor_missing > 0 ? `${health.factor_missing} still without a factor` : "all extractions carry a SEFR factor"} tone={health.factor_missing > 0 ? "amber" : "green"} testid="ih-factors" />
            <HealthTile label="Unmapped facilities" value={health.facility_unmapped} sub={health.facility_unmapped > 0 ? "raw address, no canonical site" : "all sites resolve to canonical tags"} tone={health.facility_unmapped > 0 ? "amber" : "green"} testid="ih-facilities" />
            <HealthTile label="Zero-emission commits" value={health.zero_emission_commits} sub={health.zero_emission_commits > 0 ? "confirmed with no factor (0 tCO₂e)" : "no factor-less data points committed"} tone={health.zero_emission_commits > 0 ? "red" : "green"} testid="ih-zero-commits" />
          </div>
        </div>
      )}

      {/* Extraction review dialog */}
      <Dialog open={!!extraction} onOpenChange={(o) => !o && (setExtraction(null), setItems([]), setReplaceMode(false))}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          {extraction && (
            <>
              <DialogHeader>
                <DialogTitle>{extraction.status === "extraction_failed" ? "Extraction failed — enter fields manually" : `Review ${items.length} utilit${items.length === 1 ? "y" : "ies"} on this bill`}</DialogTitle>
                <div className="text-xs font-mono text-muted-foreground mt-1">{extraction.filename}</div>
              </DialogHeader>

              {dup && (
                <div className="text-xs border border-[#FFB800]/50 bg-[#FFB800]/10 text-[#FFB800] rounded-sm px-3 py-2 flex items-start gap-2" data-testid="duplicate-warning-banner">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    This bill looks like a re-upload{dup.invoice_number ? ` of invoice ${dup.invoice_number}` : ""}{dup.filename ? ` (already uploaded as "${dup.filename}")` : ""}{dup.committed ? " — it is already committed to your inventory" : " — it is already in your inbox"}. Committing it again will be blocked to prevent double-counting.
                    {dup.committed && (
                      <label className="mt-2 flex items-start gap-2 cursor-pointer text-[#FFB800]" data-testid="replace-toggle">
                        <input type="checkbox" checked={replaceMode} onChange={(e) => setReplaceMode(e.target.checked)} className="mt-0.5" data-testid="replace-checkbox" />
                        <span>This is a <b>corrected / supplementary</b> bill — <b>Replace</b> the committed record. The old record is kept in change history but excluded from every total.</span>
                      </label>
                    )}
                    {dup.committed && Object.keys(replaceTargets).length > 0 && (
                      <div className="mt-2 space-y-1.5" data-testid="bill-diff">
                        {Object.entries(replaceTargets).map(([i, t]) => {
                          const f = items[i]; if (!f) return null;
                          return (
                            <div key={i} className="text-[11px] bg-black/25 rounded-sm px-2.5 py-2 text-foreground/90" data-testid={`bill-diff-${i}`}>
                              <div className="font-semibold text-[#FFB800] mb-1">{f.activity_type || "Utility"} · {f.period}</div>
                              <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                                <div className="text-right">
                                  <div className="text-[9px] uppercase tracking-wide text-muted-foreground">Committed (current)</div>
                                  <div className="font-mono" data-testid={`bill-diff-old-value-${i}`}>{formatNum(t.value)} {t.unit}</div>
                                  <div className="font-mono">{t.currency_amount != null ? `${t.currency || ""} ${formatNum(t.currency_amount)}` : "no cost on file"}</div>
                                </div>
                                <ArrowRight className="h-4 w-4 text-[#FFB800] shrink-0" />
                                <div>
                                  <div className="text-[9px] uppercase tracking-wide text-muted-foreground">This bill (new)</div>
                                  <div className="font-mono" data-testid={`bill-diff-new-value-${i}`}>{f.value !== "" ? formatNum(Number(f.value)) : "—"} {f.unit}</div>
                                  <div className="font-mono">{f.currency_amount !== "" && f.currency_amount != null ? `${f.currency || ""} ${formatNum(Number(f.currency_amount))}` : "no cost"}</div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {items.map((f, idx) => {
                  const confirmed = f.item_status === "confirmed";
                  return (
                    <div key={f.item_id || idx} className={`card-flat p-4 ${confirmed ? "opacity-60" : ""}`} data-testid={`utility-item-${idx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-display font-semibold text-sm">{f.activity_type || f.document_type || `Utility ${idx + 1}`}</span>
                          <Badge variant={f.confidence === "high" ? "success" : f.confidence === "medium" ? "warning" : "danger"} className="text-[10px]">{f.confidence} confidence</Badge>
                        </div>
                        {confirmed && <Badge variant="success" className="text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1" /> Committed</Badge>}
                      </div>

                      {f.extraction_notes && (
                        <div className="text-[11px] italic border-l-2 border-accent pl-2 text-muted-foreground mb-2" data-testid={`extract-notes-${idx}`}>{f.extraction_notes}</div>
                      )}

                      {f.reconcile && (() => {
                        const r = f.reconcile; const cur = r.currency || "";
                        const num = (v) => (v == null ? "—" : `${v}`);
                        if (r.status === "identical")
                          return <div className="text-[11px] rounded-sm px-2 py-1.5 mb-2 border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 flex items-center gap-1.5" data-testid={`reconcile-${idx}`}><CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> Matches the previously confirmed bill exactly — identical reading, no re-keying needed.</div>;
                        if (r.status === "auto_reconciled")
                          return <div className="text-[11px] rounded-sm px-2 py-1.5 mb-2 border border-sky-500/40 bg-sky-500/10 text-sky-300" data-testid={`reconcile-${idx}`}>
                            <div className="flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5 shrink-0" /> Auto-reconciled to the previously confirmed figure so this re-upload reads identically (OCR drift {r.value_delta_pct != null ? `${r.value_delta_pct}%` : "small"} on value{r.cost_delta_pct != null ? `, ${r.cost_delta_pct}% on cost` : ""}).</div>
                            <button type="button" className="underline mt-1 hover:text-sky-100" disabled={confirmed} data-testid={`reconcile-use-new-${idx}`}
                              onClick={() => setItem(idx, { value: r.new_value ?? f.value, currency_amount: r.new_cost ?? f.currency_amount, reconcile: { ...r, status: "mismatch" } })}>Use the newly scanned figure instead</button>
                          </div>;
                        return <div className="text-[11px] rounded-sm px-2 py-1.5 mb-2 border border-amber-500/40 bg-amber-500/10 text-amber-300" data-testid={`reconcile-${idx}`}>
                          <div className="flex items-center gap-1.5 font-medium"><AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Differs from the previously confirmed bill — check which is right.</div>
                          <div className="mt-1 font-mono text-[10px] text-amber-200/90">Value: prior {num(r.prior_value)} → new {num(r.new_value)}{r.value_delta_pct != null ? ` (${r.value_delta_pct > 0 ? "+" : ""}${r.value_delta_pct}%)` : ""}{r.prior_cost != null || r.new_cost != null ? ` · Cost: prior ${cur} ${num(r.prior_cost)} → new ${cur} ${num(r.new_cost)}` : ""}</div>
                          <div className="flex gap-3 mt-1">
                            <button type="button" className="underline hover:text-amber-100" disabled={confirmed} data-testid={`reconcile-use-prior-${idx}`}
                              onClick={() => setItem(idx, { value: r.prior_value ?? f.value, currency_amount: r.prior_cost ?? f.currency_amount, reconcile: { ...r, status: "auto_reconciled" } })}>Use previously confirmed figure</button>
                            <button type="button" className="underline hover:text-amber-100" disabled={confirmed} data-testid={`reconcile-keep-new-${idx}`}
                              onClick={() => setItem(idx, { reconcile: null })}>Keep the new figure</button>
                          </div>
                        </div>;
                      })()}

                      {f.date_flag && f.date_flag !== "no_period" && (
                        <div className="text-[11px] rounded-sm px-2 py-1 mb-2 border border-amber-500/40 bg-amber-500/10 text-amber-400" data-testid={`date-flag-${idx}`}>
                          {f.date_flag === "end_before_start" ? "Billing end date is before the start date — please correct the period."
                            : f.date_flag === "unusual_length" ? "Billing period isn't a typical month (check the start/end dates)."
                            : "Billing dates couldn't be parsed — please set the period start and end."}
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>Vendor</Label><Input value={f.vendor} disabled /></div>
                        <div>
                          <Label>Scope</Label>
                          <Select value={String(f.scope)} onValueChange={(v) => setItem(idx, { scope: v })} disabled={confirmed}>
                            <SelectTrigger data-testid={`ex-scope-${idx}`}><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="1">Scope 1</SelectItem><SelectItem value="2">Scope 2</SelectItem><SelectItem value="3">Scope 3</SelectItem></SelectContent>
                          </Select>
                        </div>
                        <div><Label>Period start</Label><Input type="date" value={f.period_start} onChange={(e) => setItem(idx, { period_start: e.target.value })} disabled={confirmed} data-testid={`ex-period-start-${idx}`} /></div>
                        <div><Label>Period end</Label><Input type="date" value={f.period_end} onChange={(e) => setItem(idx, { period_end: e.target.value })} disabled={confirmed} data-testid={`ex-period-end-${idx}`} /></div>
                        <div className="col-span-2 -mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid={`period-basis-${idx}`}>
                          <CalendarRange className="h-3 w-3 text-accent" /> Period basis: <b className="text-foreground">Period-end billing</b> — reporting year follows the end date{f.period ? ` (→ ${f.period})` : ""}.
                        </div>
                        <div className="col-span-2"><Label>Category</Label><Input value={f.category} onChange={(e) => setItem(idx, { category: e.target.value })} disabled={confirmed} data-testid={`ex-category-${idx}`} /></div>
                        <div className="col-span-2"><Label>Activity type</Label><Input value={f.activity_type} onChange={(e) => setItem(idx, { activity_type: e.target.value })} disabled={confirmed} data-testid={`ex-activity-${idx}`} /></div>
                        <div><Label>Value</Label><Input type="number" step="any" value={f.value} onChange={(e) => setItem(idx, { value: e.target.value })} disabled={confirmed} data-testid={`ex-value-${idx}`} /></div>
                        <div><Label>Unit</Label><Input value={f.unit} onChange={(e) => setItem(idx, { unit: e.target.value })} disabled={confirmed} data-testid={`ex-unit-${idx}`} /></div>
                        <div><Label>Bill cost</Label><Input type="number" step="any" value={f.currency_amount} onChange={(e) => setItem(idx, { currency_amount: e.target.value })} disabled={confirmed} placeholder="e.g. 411.81" data-testid={`ex-cost-${idx}`} /></div>
                        <div><Label>Currency</Label><Input value={f.currency} onChange={(e) => setItem(idx, { currency: e.target.value })} disabled={confirmed} data-testid={`ex-currency-${idx}`} /></div>
                        <div className="col-span-2">
                          <Label>Meter read type</Label>
                          <Select value={f.read_type || "actual"} onValueChange={(v) => setItem(idx, { read_type: v })} disabled={confirmed}>
                            <SelectTrigger data-testid={`ex-readtype-${idx}`}><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="actual">Actual read</SelectItem><SelectItem value="estimated">Estimated read</SelectItem></SelectContent>
                          </Select>
                          <div className="text-[11px] text-muted-foreground mt-0.5">Taken from the bill; correct it here if the reading was estimated.</div>
                        </div>
                        <div className="col-span-2">
                          <Label>Emission factor (SEFR)</Label>
                          <Select value={f.factor_code || "none"} onValueChange={(v) => setItem(idx, { factor_code: v === "none" ? "" : v })} disabled={confirmed}>
                            <SelectTrigger data-testid={`ex-factor-${idx}`}><SelectValue placeholder="Optional" /></SelectTrigger>
                            <SelectContent className="max-h-64"><SelectItem value="none">No factor</SelectItem>{factors.map((ff) => <SelectItem key={ff.code} value={ff.code}>{ff.code} — {ff.name}</SelectItem>)}</SelectContent>
                          </Select>
                          {f.factor_code && f.factor_resolution && f.factor_code === f.suggested_factor_code && (
                            <div className="text-[11px] text-emerald-500 mt-1" data-testid={`factor-resolved-note-${idx}`}>✓ {f.factor_resolution}</div>
                          )}
                        </div>
                        <div className="col-span-2">
                          <Label>Facility</Label>
                          {(f.facility_options || []).length > 0 ? (
                            <Select value={f.facility || "none"} onValueChange={(v) => setItem(idx, { facility: v === "none" ? "" : v })} disabled={confirmed}>
                              <SelectTrigger data-testid={`ex-facility-${idx}`}><SelectValue placeholder="Select facility" /></SelectTrigger>
                              <SelectContent className="max-h-64">
                                <SelectItem value="none">—</SelectItem>
                                {f.facility_options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                                {f.facility && !f.facility_options.includes(f.facility) && f.facility !== f.facility_raw && <SelectItem value={f.facility}>{f.facility} (new site)</SelectItem>}
                                {f.facility_raw && !f.facility_options.includes(f.facility_raw) && f.facility_raw !== f.facility && <SelectItem value={f.facility_raw}>As extracted: {f.facility_raw}</SelectItem>}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input value={f.facility} onChange={(e) => setItem(idx, { facility: e.target.value })} disabled={confirmed} data-testid={`ex-facility-${idx}`} />
                          )}
                          {f.service_address && <div className="text-[11px] text-muted-foreground mt-1" data-testid={`facility-premise-${idx}`}>Bill premise: {f.service_address}{f.account_number ? ` · acct ${f.account_number}` : ""}</div>}
                          {f.facility_match_source && f.facility_resolved && (
                            <div className="text-[11px] text-emerald-500 mt-0.5" data-testid={`facility-automatch-${idx}`}>✓ Auto-matched ({f.facility_match_source})</div>
                          )}
                          {f.facility_is_new_site && !(f.facility_options || []).includes(f.facility) && (
                            <div className="mt-1.5" data-testid={`facility-newsite-block-${idx}`}>
                              <Label>New site name</Label>
                              <Input value={f.facility} onChange={(e) => setItem(idx, { facility: e.target.value })} disabled={confirmed} data-testid={`ex-newsite-name-${idx}`} placeholder="Name this new site" />
                              <div className="mt-1.5">
                                <Label>Group</Label>
                                <Select value={f.group_id || "__none__"} onValueChange={(v) => setItem(idx, { group_id: v === "__none__" ? "" : v })} disabled={confirmed}>
                                  <SelectTrigger data-testid={`ex-newsite-group-${idx}`}><SelectValue placeholder="Ungrouped" /></SelectTrigger>
                                  <SelectContent className="max-h-64">
                                    <SelectItem value="__none__">Ungrouped</SelectItem>
                                    {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.label || g.name}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="text-[11px] text-sky-500 mt-0.5" data-testid={`facility-newsite-${idx}`}>New site from this bill's premise — pick its group and edit the name if you like. Confirming creates a reusable site and remembers it for next month.</div>
                            </div>
                          )}
                          {f.facility_ambiguous && !f.facility && (
                            <div className="text-[11px] border border-amber-500/40 bg-amber-500/10 text-amber-500 rounded-sm px-2 py-1.5 mt-1" data-testid={`facility-ambiguous-${idx}`}>
                              Several sites share this bill's postal code and none matched on street/unit — pick the correct site above before confirming.
                            </div>
                          )}
                          {f.facility_raw && f.facility_resolved === false && !f.facility_is_new_site && !f.facility_ambiguous && (
                            <div className="text-[11px] text-amber-500 mt-1" data-testid={`facility-unresolved-note-${idx}`}>"{f.facility_raw}" is not a known site — pick a canonical facility.</div>
                          )}
                        </div>
                      </div>

                      {!f.factor_code && !confirmed && (
                        <div className="text-[11px] border border-amber-500/40 bg-amber-500/10 text-amber-500 rounded-sm px-2 py-1.5 mt-2" data-testid={`zero-emissions-warning-${idx}`}>
                          No emission factor — this utility will commit with <b>0 tCO₂e</b>. Choose a SEFR factor unless it is intentionally non-emission.
                        </div>
                      )}

                      {!confirmed && (
                        <Button variant="primary" onClick={() => confirmItem(idx)} className="w-full mt-3" disabled={!f.value || confirming} data-testid={`ex-item-confirm-${idx}`}>
                          <CheckCircle2 className="h-4 w-4" /> {confirming ? (replaceMode ? "Replacing…" : "Committing…") : (replaceMode ? `Replace & commit ${f.activity_type || "this utility"} (Unverified)` : `Confirm ${f.activity_type || "this utility"} (Unverified)`)}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>

              <Button variant="outline" onClick={() => { setExtraction(null); setItems([]); setReplaceMode(false); }} className="w-full mt-1" data-testid="ex-close-btn">Done</Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* History */}
      <div className="card-flat overflow-hidden">
        <div className="p-4 border-b border-border bg-surface"><div className="label-eyebrow">Recent uploads — {docs.length}</div></div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead></TableHead><TableHead>Filename</TableHead><TableHead>Utilities</TableHead>
              <TableHead>Activity</TableHead><TableHead className="text-right">Value</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {docs.map((d) => {
              const isHi = highlightId === d.id;
              const list = d.extracted_items || (d.extracted ? [d.extracted] : []);
              return (
                <TableRow key={d.id} data-testid="doc-row" ref={isHi ? highlightRef : null} className={isHi ? "bg-accent/10 ring-1 ring-accent/50 transition-colors" : ""}>
                  <TableCell>{d.mime?.startsWith("image/") ? <ImageIcon className="h-4 w-4 text-muted-foreground" /> : <FileType className="h-4 w-4 text-muted-foreground" />}</TableCell>
                  <TableCell className="font-mono text-xs">{d.filename}</TableCell>
                  <TableCell className="text-xs">{list.map((i) => i.activity_type).filter(Boolean).join(", ") || "—"}</TableCell>
                  <TableCell className="text-xs">{d.extracted?.activity_type || "—"}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{d.extracted?.value ? formatNum(d.extracted.value) : "—"} {d.extracted?.unit}</TableCell>
                  <TableCell>
                    {d.status === "confirmed" ? <Badge variant="success">Confirmed</Badge> :
                     d.status === "partially_confirmed" ? <Badge variant="warning">Partly confirmed</Badge> :
                     d.status === "extraction_failed" ? <Badge variant="danger">Failed — manual entry</Badge> :
                     <Badge variant="warning">Pending</Badge>}
                    {d.duplicate_warning && <Badge variant="danger" className="ml-1 text-[9px]" data-testid="doc-dup-badge">Duplicate</Badge>}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-0.5">
                      {(d.status === "pending_review" || d.status === "partially_confirmed") && (
                        <Button variant="ghost" size="icon" onClick={() => reopen(d.id)} data-testid="reopen-doc-btn" title="Reopen this upload to finish reviewing & confirming">
                          <PencilLine className="h-3.5 w-3.5 text-accent" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => remove(d.id)} data-testid="delete-doc-btn"><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {docs.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No documents yet — upload your first bill.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function HealthTile({ label, value, sub, tone, testid }) {
  const color = tone === "red" ? "text-red-400" : tone === "amber" ? "text-amber-400" : tone === "green" ? "text-[#00E65B]" : "";
  return (
    <div className="card-flat p-4" data-testid={testid}>
      <div className="label-eyebrow text-[10px]">{label}</div>
      <div className={`font-mono text-2xl font-bold mt-1 ${color}`}>{value}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>
    </div>
  );
}
